/**
 * Passthrough (feature guide)
 *
 * Screenshots were taken from the Sokuji v0.44.0 desktop app.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "Passthrough",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "Passthrough mixes your own voice into the virtual microphone under the translation, so the other side hears your original voice quietly beneath the translated one."
  },
  "steps": [
    {
      "title": "Switch it on",
      "content": "In Settings, in the <strong>Speech</strong> block (Quick settings, or Advanced on the <strong>Audio</strong> tab), switch on <strong>Passthrough</strong>, the row under <strong>Translation the other side hears</strong>. Set its volume with the slider, from 0% to 60% (<strong>Volume 20%</strong> by default). You can change both during a session.",
      "screenshot": "01-passthrough.png"
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "Keep the volume low, so that the translation stays easy to follow.",
      "It only matters when the other side hears you through the virtual microphone: in Me or Both mode, with Translation the other side hears on. Otherwise the row is greyed and silent.",
      "With providers that offer Push-to-Translate, that mode manages passthrough by itself.",
      "Passthrough is not offered face-to-face, where the other person hears your own voice in the room."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "Why would I want this?",
        "answer": "Hearing your real voice, even quietly, helps the other side follow who is speaking and your tone."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "原声直通",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "原声直通会把你自己的声音混入虚拟麦克风，放在翻译之下，让对方在翻译语音之下隐约听到你的原声。"
  },
  "steps": [
    {
      "title": "开启",
      "content": "在设置的<strong>语音</strong>区块中（“快捷”设置，或“高级”的<strong>音频</strong>标签页），开启<strong>原声直通</strong>，它在<strong>对方听到的翻译</strong>下面一行。用滑块设置音量，范围为 0% 到 60%（默认<strong>音量 20%</strong>）。两者都可以在会话期间更改。",
      "screenshot": "01-passthrough.png"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "请保持较低的音量，这样翻译仍然容易听清。",
      "只有当对方通过虚拟麦克风听你说话时才有作用：在“我”或“两者”模式下，并且开启了“对方听到的翻译”。否则这一行会变灰且不发声。",
      "对于提供按住翻译的服务商，该模式会自行管理原声直通。",
      "面对面时不提供原声直通，因为对方在现场就能听到你本人的声音。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "为什么需要这个功能？",
        "answer": "即使音量很低，听到你真实的声音也能帮助对方分辨是谁在说话以及你的语气。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "原音パススルー",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "原音パススルーは、あなた自身の声を翻訳の下に重ねて仮想マイクに混ぜます。相手には、翻訳音声の下にあなたの元の声が小さく聞こえます。"
  },
  "steps": [
    {
      "title": "オンにする",
      "content": "設定の<strong>音声</strong>ブロック（「クイック」設定、または「詳細」の<strong>オーディオ</strong>タブ）で、<strong>相手に聞こえる翻訳</strong>の下の行にある<strong>原音パススルー</strong>をオンにします。スライダーで音量を 0%〜60% の範囲で設定します（既定は<strong>音量 20%</strong>）。どちらもセッション中に変更できます。",
      "screenshot": "01-passthrough.png"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "翻訳が聞き取りやすいよう、音量は低めにしてください。",
      "効果があるのは、相手が仮想マイクを通じてあなたの声を聞く場合だけです。つまり、自分モードまたは両方モードで「相手に聞こえる翻訳」がオンのときです。それ以外では、この行はグレー表示になり、音も出ません。",
      "プッシュトゥトランスレートに対応したプロバイダーでは、そのモードが原音パススルーを自動で管理します。",
      "対面では原音パススルーは表示されません。相手はその場であなたの声を直接聞けるからです。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "なぜこの機能を使うのですか？",
        "answer": "小さな音量でも本人の声が聞こえると、相手は誰が話しているのかや、あなたの口調をつかみやすくなります。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "원음 패스스루",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "원음 패스스루는 내 목소리를 번역 아래에 깔아 가상 마이크에 섞어, 상대방이 번역된 음성 아래로 내 원래 목소리를 작게 들을 수 있게 합니다."
  },
  "steps": [
    {
      "title": "켜기",
      "content": "설정의 <strong>음성</strong> 블록(“빠른” 설정 또는 “고급”의 <strong>오디오</strong> 탭)에서 <strong>상대방에게 들리는 번역</strong> 아래 행에 있는 <strong>원음 패스스루</strong>를 켜세요. 슬라이더로 음량을 0%~60% 범위에서 설정하세요(기본값 <strong>음량 20%</strong>). 둘 다 세션 중에 바꿀 수 있습니다.",
      "screenshot": "01-passthrough.png"
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "번역을 알아듣기 쉽도록 볼륨을 낮게 유지하세요.",
      "상대방이 가상 마이크를 통해 내 말을 들을 때, 즉 나 모드나 모두 모드에서 “상대방에게 들리는 번역”이 켜져 있을 때만 의미가 있습니다. 그 밖의 경우에는 이 행이 회색으로 표시되고 소리가 나지 않습니다.",
      "푸시 투 트랜슬레이트를 제공하는 제공업체에서는 그 모드가 원음 패스스루를 알아서 관리합니다.",
      "대면 대화에서는 원음 패스스루가 제공되지 않습니다. 상대방이 그 자리에서 내 목소리를 직접 듣기 때문입니다."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "이 기능은 왜 필요한가요?",
        "answer": "작게라도 내 실제 목소리가 들리면 상대방이 누가 말하고 있는지와 내 어조를 파악하는 데 도움이 됩니다."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "Paso directo",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "Paso directo mezcla tu propia voz con la traducción en el micrófono virtual, para que la otra parte oiga tu voz original a bajo volumen por debajo de la traducida."
  },
  "steps": [
    {
      "title": "Actívalo",
      "content": "En Configuración, en el bloque <strong>Voz</strong> (Rápido o, en Avanzado, la pestaña <strong>Audio</strong>), activa <strong>Paso directo</strong>, la fila que está debajo de <strong>Traducción que oye la otra parte</strong>. Ajusta su volumen con el control deslizante, del 0 % al 60 % (<strong>Volumen 20 %</strong> de forma predeterminada). Puedes cambiar ambos durante una sesión.",
      "screenshot": "01-passthrough.png"
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "Mantén el volumen bajo para que la traducción siga siendo fácil de seguir.",
      "Solo tiene efecto cuando la otra parte te oye a través del micrófono virtual: en el modo Yo o Ambos, con Traducción que oye la otra parte activada. Si no, la fila aparece en gris y en silencio.",
      "Con los proveedores que ofrecen Pulsar para traducir, ese modo gestiona el Paso directo por sí solo.",
      "Paso directo no se ofrece en una conversación cara a cara, donde la otra persona oye tu propia voz en la sala."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "¿Para qué me sirve esto?",
        "answer": "Oír tu voz real, aunque sea a bajo volumen, ayuda a la otra parte a saber quién habla y a captar tu tono."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "Passage direct",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "Le passage direct mêle votre propre voix à la traduction dans le microphone virtuel, pour que votre interlocuteur entende discrètement votre voix originale sous la voix traduite."
  },
  "steps": [
    {
      "title": "Activez-le",
      "content": "Dans les Paramètres, dans le bloc <strong>Voix</strong> (Rapide, ou Avancé sous l’onglet <strong>Audio</strong>), activez <strong>Passage direct</strong>, la ligne sous <strong>Traduction entendue par l'autre</strong>. Réglez son volume avec le curseur, de 0 % à 60 % (<strong>Volume 20 %</strong> par défaut). Vous pouvez changer les deux pendant une session.",
      "screenshot": "01-passthrough.png"
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "Gardez un volume bas, pour que la traduction reste facile à suivre.",
      "Cela n’a d’effet que lorsque votre interlocuteur vous entend via le microphone virtuel : en mode Moi ou Les deux, avec Traduction entendue par l'autre activée. Sinon, la ligne est grisée et muette.",
      "Avec les fournisseurs qui proposent Appuyer pour traduire, ce mode gère lui-même le Passage direct.",
      "Le passage direct n’est pas proposé en face à face, où l’autre personne entend votre propre voix dans la pièce."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "À quoi cela sert-il ?",
        "answer": "Entendre votre vraie voix, même discrètement, aide votre interlocuteur à savoir qui parle et à percevoir votre ton."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "التمرير المباشر",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "يمزج التمرير المباشر صوتك أنت في الميكروفون الافتراضي تحت الترجمة، فيسمع الطرف الآخر صوتك الأصلي بهدوء تحت الصوت المترجَم."
  },
  "steps": [
    {
      "title": "فعّله",
      "content": "في الإعدادات، في قسم <strong>الكلام</strong> (إعدادات «سريع»، أو «متقدم» ضمن علامة التبويب <strong>الصوت</strong>)، فعّل <strong>التمرير المباشر</strong>، وهو الصف الذي تحت <strong>الترجمة التي يسمعها الطرف الآخر</strong>. واضبط مستوى صوته بالمنزلق، من 0% إلى 60% (<strong>مستوى الصوت 20%</strong> افتراضيًا). يمكنك تغيير الاثنين أثناء الجلسة.",
      "screenshot": "01-passthrough.png"
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "أبقِ مستوى الصوت منخفضًا، حتى تظل الترجمة سهلة المتابعة.",
      "لا يهم إلا عندما يسمعك الطرف الآخر عبر الميكروفون الافتراضي: في وضع أنا أو الكل، مع تفعيل «الترجمة التي يسمعها الطرف الآخر». وإلا يكون الصف باللون الرمادي وصامتًا.",
      "مع مزوّدي الخدمة الذين يوفّرون «اضغط للترجمة»، يدير ذلك الوضع التمرير المباشر بنفسه.",
      "لا يُعرض التمرير المباشر في المحادثة وجهًا لوجه، حيث يسمع الشخص الآخر صوتك أنت في الغرفة."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "لماذا قد أحتاج إلى هذا؟",
        "answer": "سماع صوتك الحقيقي، ولو بهدوء، يساعد الطرف الآخر على معرفة من يتحدث وعلى إدراك نبرتك."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "Passagem direta",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "A Passagem direta mistura a sua própria voz à tradução no microfone virtual, para que a outra pessoa ouça sua voz original em volume baixo, por baixo da voz traduzida."
  },
  "steps": [
    {
      "title": "Ative a opção",
      "content": "Em Configurações, no bloco <strong>Voz</strong> (Rápido ou, em Avançado, a aba <strong>Áudio</strong>), ative <strong>Passagem direta</strong>, a linha abaixo de <strong>Tradução que o outro lado ouve</strong>. Ajuste o volume com o controle deslizante, de 0% a 60% (<strong>Volume 20%</strong> por padrão). Você pode alterar os dois durante uma sessão.",
      "screenshot": "01-passthrough.png"
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "Mantenha o volume baixo, para que a tradução continue fácil de acompanhar.",
      "Só faz diferença quando a outra pessoa ouve você pelo microfone virtual: no modo Eu ou Ambos, com Tradução que o outro lado ouve ativada. Caso contrário, a linha fica esmaecida e em silêncio.",
      "Com provedores que oferecem Pressionar para traduzir, esse modo cuida da Passagem direta sozinho.",
      "A Passagem direta não é oferecida em uma conversa cara a cara, em que a outra pessoa ouve a sua própria voz no mesmo ambiente."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "Por que eu usaria isso?",
        "answer": "Ouvir a sua voz real, mesmo em volume baixo, ajuda a outra pessoa a perceber quem está falando e o seu tom."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Passthrough",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "Функция Passthrough подмешивает ваш собственный голос в виртуальный микрофон вместе с переводом, так что собеседник тихо слышит ваш исходный голос под переведённым."
  },
  "steps": [
    {
      "title": "Включите её",
      "content": "В настройках, в блоке <strong>Речь</strong> (в режиме «Быстрый» или в режиме «Расширенный» на вкладке <strong>Аудио</strong>), включите <strong>Passthrough</strong> — строку под строкой <strong>Перевод, который слышит собеседник</strong>. Задайте громкость ползунком от 0% до 60% (по умолчанию <strong>Громкость 20%</strong>). Оба параметра можно менять во время сеанса.",
      "screenshot": "01-passthrough.png"
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "Держите громкость низкой, чтобы перевод было легко воспринимать.",
      "Это имеет значение, только когда собеседник слышит вас через виртуальный микрофон: в режиме «Я» или «Оба», при включённом «Перевод, который слышит собеседник». Иначе строка серая и беззвучная.",
      "У провайдеров с режимом «Нажми и переведи» этот режим сам управляет функцией Passthrough.",
      "Функция Passthrough недоступна при разговоре лицом к лицу: собеседник и так слышит ваш собственный голос в комнате."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Зачем это нужно?",
        "answer": "Слыша ваш настоящий голос, пусть и тихо, собеседнику легче понять, кто говорит, и уловить вашу интонацию."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Durchleitung",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "Die Durchleitung mischt Ihre eigene Stimme im virtuellen Mikrofon unter die Übersetzung, sodass Ihr Gegenüber Ihre Originalstimme leise unter der übersetzten hört."
  },
  "steps": [
    {
      "title": "Einschalten",
      "content": "Schalten Sie in den Einstellungen im Bereich <strong>Sprache</strong> (Ansicht „Schnell“ oder „Erweitert“ im Tab <strong>Audio</strong>) <strong>Durchleitung</strong> ein, die Zeile unter <strong>Übersetzung, die das Gegenüber hört</strong>. Stellen Sie die Lautstärke mit dem Schieberegler ein, von 0 % bis 60 % (standardmäßig <strong>Lautstärke 20 %</strong>). Beides können Sie während einer Sitzung ändern.",
      "screenshot": "01-passthrough.png"
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "Halten Sie die Lautstärke niedrig, damit die Übersetzung gut verständlich bleibt.",
      "Das spielt nur eine Rolle, wenn Ihr Gegenüber Sie über das virtuelle Mikrofon hört: im Modus „Ich“ oder „Beides“, mit eingeschalteter „Übersetzung, die das Gegenüber hört“. Andernfalls ist die Zeile ausgegraut und stumm.",
      "Bei Anbietern mit Push-to-Translate steuert dieser Modus die Durchleitung selbst.",
      "Von Angesicht zu Angesicht wird die Durchleitung nicht angeboten, denn dort hört die andere Person Ihre eigene Stimme im Raum."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Wozu sollte ich das wollen?",
        "answer": "Ihre echte Stimme zu hören, auch leise, hilft Ihrem Gegenüber zu erkennen, wer spricht, und Ihren Tonfall mitzubekommen."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Passthrough",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "Функція Passthrough підмішує ваш власний голос у віртуальний мікрофон разом із перекладом, тож співрозмовник тихо чує ваш оригінальний голос під перекладеним."
  },
  "steps": [
    {
      "title": "Увімкніть її",
      "content": "У налаштуваннях, у блоці <strong>Мовлення</strong> (у режимі «Швидкий» або в режимі «Розширений» на вкладці <strong>Аудіо</strong>), увімкніть <strong>Passthrough</strong> — рядок під рядком <strong>Переклад, який чує співрозмовник</strong>. Задайте гучність повзунком від 0% до 60% (за замовчуванням <strong>Гучність 20%</strong>). Обидва параметри можна змінювати під час сеансу.",
      "screenshot": "01-passthrough.png"
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "Тримайте гучність низькою, щоб переклад було легко сприймати.",
      "Це має значення лише тоді, коли співрозмовник чує вас через віртуальний мікрофон: у режимі «Я» або «Обидва», з увімкненим «Переклад, який чує співрозмовник». Інакше рядок сірий і беззвучний.",
      "У провайдерів із режимом «Натисни і переклади» цей режим сам керує функцією Passthrough.",
      "Функція Passthrough недоступна під час розмови віч-на-віч: співрозмовник і так чує ваш власний голос у кімнаті."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Навіщо це потрібно?",
        "answer": "Чуючи ваш справжній голос, хай і тихо, співрозмовникові легше зрозуміти, хто говорить, і вловити вашу інтонацію."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "Passthrough",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "Il Passthrough mescola la tua voce alla traduzione nel microfono virtuale, così l’altra parte sente la tua voce originale a basso volume sotto quella tradotta."
  },
  "steps": [
    {
      "title": "Attivalo",
      "content": "In Impostazioni, nel blocco <strong>Voce</strong> (Rapido o, in Avanzato, la scheda <strong>Audio</strong>), attiva <strong>Passthrough</strong>, la riga sotto <strong>Traduzione che sente l'altra parte</strong>. Regola il volume con il cursore, dallo 0% al 60% (<strong>Volume 20%</strong> per impostazione predefinita). Puoi cambiare entrambi durante una sessione.",
      "screenshot": "01-passthrough.png"
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "Tieni il volume basso, così la traduzione resta facile da seguire.",
      "Conta solo quando l’altra parte ti sente tramite il microfono virtuale: in modalità Io o Entrambi, con Traduzione che sente l'altra parte attiva. Altrimenti la riga è in grigio e muta.",
      "Con i fornitori che offrono Premi per tradurre, quella modalità gestisce da sola il Passthrough.",
      "Il Passthrough non è disponibile nel faccia a faccia, dove l’altra persona sente la tua voce direttamente nella stanza."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "A cosa serve?",
        "answer": "Sentire la tua voce reale, anche a basso volume, aiuta l’altra parte a capire chi sta parlando e a cogliere il tuo tono."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function FeaturePassthrough() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/passthrough" />;
}
